// Shared subscription activity events. Used by the dashboard's Subscription
// Activity card, and intended for the Activity Log page to read the same events.

export type SubscriptionActivityEventType = 'Subscription' | 'Payment' | 'Cancellation';

export type SubscriptionActivityKind = 'Payment method added' | 'Payment method updated' | 'Subscription paused' | 'Subscription cancelled' | 'Subscription resumed' | 'Shipping address updated';

export interface SubscriptionActivityEvent {
  id: string;
  eventType: SubscriptionActivityEventType;
  kind: SubscriptionActivityKind;
  description: string;
  memberName: string;
  subscriptionId: string;
  hoursAgo: number;
}

export interface SubscriptionActivityView {
  id: string;
  name: string;
  // Empty list means every subscription change.
  events: SubscriptionActivityKind[];
}

// Same colours as the Activity Log's event type badges.
export const subscriptionEventTypeBadge: Record<SubscriptionActivityEventType, {
  bg: string;
  text: string;
}> = {
  Subscription: {
    bg: '#DBEAFE',
    text: '#1E40AF'
  },
  Payment: {
    bg: '#FEF3C7',
    text: '#92400E'
  },
  Cancellation: {
    bg: '#FEE2E2',
    text: '#B91C1C'
  }
};

export const subscriptionActivityViews: SubscriptionActivityView[] = [{
  id: 'payment-methods',
  name: 'Payment method changes',
  events: ['Payment method added', 'Payment method updated']
}, {
  id: 'cancellations-pauses',
  name: 'Cancellations and pauses',
  events: ['Subscription cancelled', 'Subscription paused']
}, {
  id: 'address-changes',
  name: 'Address changes',
  events: ['Shipping address updated']
}, {
  id: 'all',
  name: 'All subscription changes',
  events: []
}];

export const DEFAULT_SUBSCRIPTION_ACTIVITY_VIEW = 'all';

// Newest first, spread over the last three days.
export const subscriptionActivityEvents: SubscriptionActivityEvent[] = [{
  id: 'sa-001',
  eventType: 'Payment',
  kind: 'Payment method updated',
  description: 'Payment method updated',
  memberName: 'Olivia Hart',
  subscriptionId: 'SUB-23141',
  hoursAgo: 2
}, {
  id: 'sa-002',
  eventType: 'Subscription',
  kind: 'Subscription paused',
  description: 'Subscription paused, reason: Travelling',
  memberName: 'Lachlan Reid',
  subscriptionId: 'SUB-23137',
  hoursAgo: 4
}, {
  id: 'sa-003',
  eventType: 'Subscription',
  kind: 'Shipping address updated',
  description: 'Shipping address updated',
  memberName: 'Chloe Nguyen',
  subscriptionId: 'SUB-23129',
  hoursAgo: 7
}, {
  id: 'sa-004',
  eventType: 'Cancellation',
  kind: 'Subscription cancelled',
  description: 'Cancelled, reason: Too much wine',
  memberName: 'Mitchell Barnes',
  subscriptionId: 'SUB-23118',
  hoursAgo: 11
}, {
  id: 'sa-005',
  eventType: 'Payment',
  kind: 'Payment method added',
  description: 'Payment method added',
  memberName: 'Isla Fraser',
  subscriptionId: 'SUB-23144',
  hoursAgo: 19
}, {
  id: 'sa-006',
  eventType: 'Subscription',
  kind: 'Subscription resumed',
  description: 'Subscription resumed',
  memberName: 'Jack Sullivan',
  subscriptionId: 'SUB-23102',
  hoursAgo: 26
}, {
  id: 'sa-007',
  eventType: 'Cancellation',
  kind: 'Subscription cancelled',
  description: 'Cancelled, reason: Moving overseas',
  memberName: 'Grace Whitfield',
  subscriptionId: 'SUB-23095',
  hoursAgo: 33
}, {
  id: 'sa-008',
  eventType: 'Payment',
  kind: 'Payment method updated',
  description: 'Payment method updated',
  memberName: 'Harrison Kelly',
  subscriptionId: 'SUB-23088',
  hoursAgo: 41
}, {
  id: 'sa-009',
  eventType: 'Subscription',
  kind: 'Shipping address updated',
  description: 'Shipping address updated',
  memberName: 'Matilda Brooks',
  subscriptionId: 'SUB-23076',
  hoursAgo: 52
}, {
  id: 'sa-010',
  eventType: 'Subscription',
  kind: 'Subscription paused',
  description: 'Subscription paused, reason: Too much stock at home',
  memberName: 'Ethan Campbell',
  subscriptionId: 'SUB-23069',
  hoursAgo: 60
}, {
  id: 'sa-011',
  eventType: 'Payment',
  kind: 'Payment method added',
  description: 'Payment method added',
  memberName: 'Ruby Thompson',
  subscriptionId: 'SUB-23061',
  hoursAgo: 68
}];

export function formatRelativeHours(hoursAgo: number): string {
  if (hoursAgo < 1) return 'Just now';
  if (hoursAgo < 24) return `${hoursAgo}h ago`;
  const days = Math.floor(hoursAgo / 24);
  return `${days}d ago`;
}
